import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { CONTACT_INFO } from '../data/mock';
import { XCircle, ArrowLeft, RotateCcw, Hash } from 'lucide-react';

const PaymentFailedPage = () => {
  const [errorMessage, setErrorMessage] = useState('');
  const [orderRef, setOrderRef] = useState('');
  const location = useLocation();

  useEffect(() => {
    document.title = 'Payment Failed | Axon Muscle';
    const stored = sessionStorage.getItem('paymentError');
    if (stored) {
      setErrorMessage(stored);
      sessionStorage.removeItem('paymentError');
    }
    const params = new URLSearchParams(location.search);
    setOrderRef(params.get('ref') || '');
    sessionStorage.removeItem('pendingOrder');
  }, [location.search]);

  return (
    <div className="min-h-screen bg-neutral-950">
      <Header />

      <main className="pt-[140px] pb-20">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <div
            data-testid="payment-failed-icon"
            className="w-24 h-24 bg-gradient-to-br from-red-500 to-rose-600 rounded-2xl flex items-center justify-center mx-auto mb-8 shadow-lg shadow-red-500/20"
          >
            <XCircle className="w-12 h-12 text-white" />
          </div>

          <h1
            data-testid="payment-failed-title"
            className="font-display text-3xl md:text-4xl text-white uppercase tracking-tight mb-4"
          >
            Payment Not Completed
          </h1>

          <p
            data-testid="payment-failed-message"
            className="text-neutral-400 text-lg mb-8"
          >
            {errorMessage ||
              "We couldn't process your payment. Your card was not charged. Please try again or use a different card."}
          </p>

          {orderRef && (
            <div
              data-testid="payment-failed-ref"
              className="inline-flex items-center gap-2 bg-neutral-900 border border-neutral-800 rounded-xl px-4 py-2 mb-8 text-sm text-neutral-400"
            >
              <Hash className="w-4 h-4 text-neutral-500" />
              <span>Reference:</span>
              <span className="text-white font-mono">{orderRef}</span>
            </div>
          )}

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
            <Link to="/checkout">
              <button
                data-testid="payment-failed-retry-btn"
                className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-axon-600 to-axon-500 hover:from-axon-500 hover:to-axon-400 text-white font-bold uppercase tracking-wider rounded-xl transition-all shadow-button"
              >
                <RotateCcw className="w-5 h-5" />
                Try Again
              </button>
            </Link>
            <Link to="/shop">
              <button
                data-testid="payment-failed-shop-btn"
                className="inline-flex items-center gap-2 px-8 py-4 bg-neutral-900 hover:bg-neutral-800 border border-neutral-800 text-white font-bold uppercase tracking-wider rounded-xl transition-all"
              >
                <ArrowLeft className="w-5 h-5" />
                Back to Shop
              </button>
            </Link>
          </div>

          <div className="bg-neutral-900 border border-neutral-800 rounded-2xl p-5 text-left">
            <p className="text-sm text-neutral-400 leading-relaxed">
              Common reasons a payment fails:
            </p>
            <ul className="mt-3 text-sm text-neutral-500 space-y-1.5 list-disc list-inside">
              <li>Insufficient funds on the card</li>
              <li>Incorrect card number, expiry date, or CVV</li>
              <li>Card declined by your bank&apos;s fraud rules</li>
              <li>3D Secure verification cancelled or timed out</li>
            </ul>
            <p className="mt-4 text-xs text-neutral-500">
              Need help? Email <a href={`mailto:${CONTACT_INFO.email}`} className="text-axon-500 hover:underline">{CONTACT_INFO.email}</a>
              {' '}or call <a href={`tel:${CONTACT_INFO.phone.replace(/[^0-9]/g, '')}`} className="text-axon-500 hover:underline">{CONTACT_INFO.phone}</a>.
            </p>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PaymentFailedPage;
